/**
 * ColaDelMes — la columna lateral del mes: qué días piden algo, y la salud del período.
 *
 * POR QUÉ UNA COLA Y NO DOS KPIs
 * ------------------------------
 * «Días por cerrar: 6» dice cuántos, y ahí se acaba: para saber CUÁLES había que
 * recorrer 31 celdas a ojo comparando colores, y para saber POR QUÉ había que entrar
 * a cada una. La cola trae las tres cosas juntas —qué día, con cuánta urgencia y por
 * qué— y cada fila es el enlace a ese día. El conteo sigue estando, arriba a la
 * derecha, donde cuesta un pill y no una tarjeta.
 *
 * DOS EJES QUE NO SE PUEDEN COLAPSAR
 * ----------------------------------
 * El calendario pinta `estado` y responde «¿cerró?». Esta cola ordena por
 * `severidad`, que responde «¿tengo que ir hoy?». No son lo mismo: un día liquidado
 * con cuotas en UF sin valorizar cerró —la celda está verde y con razón— y aun así
 * hay algo que mirar. Si la cola saliera de `estado`, ese día desaparecería de la
 * vista.
 *
 * ROJO SÓLO CUANDO LA PLATA YA ESTÁ Y EL DÍA SIGUE ABIERTO. Un día esperando el
 * depósito del PSP no es una tarea, es una llamada al proveedor; lo decide `payment`
 * en `diagnosticar` y acá sólo se pinta. La regla ingenua —rojo cuando el paso
 * siguiente se puede ejecutar— pondría en rojo el día de hoy todos los días, y una
 * cola que siempre grita deja de decir algo.
 *
 * EL MOTIVO NO SE ESCRIBE ACÁ. Viene armado del paso que falta, para que la cola no
 * pueda contradecir a la pantalla del día — el mismo cuidado con que el calendario
 * comparte `armarCierreDelDia` en vez de tener su propio «está cerrado».
 *
 * @see src/components/payments/yoolpay/KpisDelMes.jsx — el otro lado: la plata del mes
 * @see payment/src/services/calendario-mes.service.ts — quién decide `severidad` y `motivo`
 */
import React from 'react';

import { Badge } from './cierre/ds';
import { Rotulo } from './cierre/piezas';
import { fmtDiaMes, fmtNum } from '../../../lib/yoolpay/formato';

/**
 * Cómo se rotula cada severidad.
 *
 * Las variantes son las del DS y NUNCA teal: el teal está reservado al CTA, y un día
 * con problemas no es una acción primaria.
 */
const SEVERIDAD = {
  sin_resolver: { variant: 'vencido', label: 'sin resolver' },
  por_mirar: { variant: 'vence', label: 'por mirar' },
};

/** La tarjeta de la columna: mismo borde y radio que `Seccion`, con aire de panel. */
const TARJETA = {
  background: '#fff',
  border: '1px solid var(--yool-border)',
  borderRadius: 16,
  overflow: 'hidden',
};

/**
 * Los días que piden algo, en orden de fecha.
 *
 * CRONOLÓGICO Y NO POR GRAVEDAD. El primero de la lista tiene que ser el mismo que
 * responde «¿por dónde sigo?» —el más antiguo sin cerrar—, porque un día viejo que
 * espera al proveedor frena la conciliación igual que uno rojo de ayer. Ordenar por
 * severidad mandaría al fondo justo al que lleva más tiempo abierto.
 */
export function diasConPendientes(dias) {
  return (dias ?? []).filter((d) => d.severidad === 'por_mirar' || d.severidad === 'sin_resolver');
}

/**
 * ¿El servicio todavía no manda `severidad`?
 *
 * tadmin y payment se despliegan por separado, así que existe una ventana en que esta
 * pantalla es más nueva que su API. Sin este chequeo, esa ventana muestra la cola vacía con
 * el texto «ningún día quedó con pendientes» sobre un mes que sí los tiene: una felicitación
 * falsa sobre plata sin girar, que es peor que no mostrar nada.
 *
 * Se mira `severidad === undefined` en TODOS los días con cobro y no `!severidad`: un día sin
 * cobros manda `null` a propósito, y confundir «no aplica» con «no vino» daría el aviso en un
 * mes tranquilo perfectamente al día.
 */
function apiSinSeveridad(dias) {
  const conCobro = (dias ?? []).filter((d) => d.cobros > 0);
  return conCobro.length > 0 && conCobro.every((d) => d.severidad === undefined);
}

/** Una fila de la cola. Es un `<button>`: la fila entera abre ese día. */
function FilaDia({ dia, onIr, ultima }) {
  const sev = SEVERIDAD[dia.severidad];
  return (
    <button
      type="button"
      onClick={() => onIr(dia.fecha)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        background: 'none',
        border: 0,
        borderBottom: ultima ? 0 : '1px solid var(--yool-gray)',
        padding: '13px 18px',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-.01em' }}>
          {fmtDiaMes(dia.fecha)}
        </span>
        {sev ? (
          <Badge variant={sev.variant} dot>
            {sev.label}
          </Badge>
        ) : null}
      </span>
      {/* EL MOTIVO VA SIEMPRE, no en un tooltip: es la razón de existir de la cola. Sin
          él la fila dice «este día tiene algo» y hay que abrirlo para saber qué. */}
      {dia.motivo ? (
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: 'var(--yool-muted)',
            marginTop: 6,
            lineHeight: 1.45,
          }}
        >
          {dia.motivo}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Una fila de salud del mes.
 *
 * EL COLOR SÓLO CUANDO HAY ALGO QUE MIRAR, o sea con un número mayor que cero. «Asientos con
 * error: 0» en rojo se lee como un problema durante el segundo que toma leer el número, y son
 * justo las filas que uno quiere poder saltar de un vistazo cuando están en cero. Un guion
 * —el dato que no vino— tampoco se pinta: no hay nada que avisar sobre lo que no se sabe.
 */
function FilaSalud({ label, valor, color, hint }) {
  const avisa = typeof valor === 'number' && valor > 0;
  return (
    <span
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      title={hint}
    >
      <span style={{ fontSize: 13, color: 'var(--yool-gray1)' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: avisa ? color : 'var(--yool-gray1)',
        }}
      >
        {fmtNum(valor)}
      </span>
    </span>
  );
}

/**
 * @param {object}   props
 * @param {object[]} props.dias     `dias` de `GET /api/mes`
 * @param {object}   props.resumen  `resumen` de `GET /api/mes`
 * @param {Function} props.onIr     `(fecha) => void` — abre ese día
 */
export default function ColaDelMes({ dias, resumen, onIr }) {
  const cola = diasConPendientes(dias);
  const limpios = (dias ?? []).filter((d) => d.severidad === 'limpio').length;
  const sinSeveridad = apiSinSeveridad(dias);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        // PEGADA AL SCROLL: la cola es la lista de tareas del calendario que está al
        // lado, y en un mes de 31 días el calendario es más alto que la ventana. Sin
        // esto habría que volver a subir para ver cuál era el día siguiente.
        position: 'sticky',
        top: 16,
      }}
    >
      <div style={TARJETA}>
        <div
          style={{
            padding: '16px 18px 13px',
            borderBottom: '1px solid var(--yool-gray)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <Rotulo>Cola de días con pendientes</Rotulo>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--yool-gray1)',
              background: 'var(--yool-gray)',
              borderRadius: 20,
              padding: '2px 9px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtNum(cola.length)}
          </span>
        </div>

        {cola.length === 0 ? (
          <div style={{ padding: '34px 20px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
              {/* TRES VACÍOS DISTINTOS, y ninguno se puede decir con las palabras de otro.
                  «Ningún día quedó con pendientes» es un logro; «el mes no ha cobrado» es
                  que todavía no hay nada; y «el servicio no manda el dato» es que la
                  pantalla no sabe. Las dos primeras frases sobre la tercera son mentira. */}
              {sinSeveridad
                ? 'El servicio todavía no informa la severidad de cada día, así que esta cola '
                  + 'no se puede armar. El calendario de al lado sí dice qué días están '
                  + 'abiertos.'
                : resumen.dias_con_movimiento === 0
                  ? 'Este mes todavía no tiene días con cobro.'
                  : 'Ningún día quedó con pendientes.'}
            </p>
          </div>
        ) : (
          <div
            className="y-cola-scroll"
            // TOPE DE ALTO Y SCROLL PROPIO: con veinte días abiertos la columna crecería
            // más que el calendario y la salud del mes quedaría bajo el pliegue.
            style={{ maxHeight: 420, overflowY: 'auto' }}
          >
            {cola.map((d, i) => (
              <FilaDia key={d.fecha} dia={d} onIr={onIr} ultima={i === cola.length - 1} />
            ))}
          </div>
        )}
      </div>

      <div style={{ ...TARJETA, padding: 18 }}>
        <div style={{ marginBottom: 12 }}>
          <Rotulo>Salud del mes</Rotulo>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          <FilaSalud
            label="Días con cobro"
            valor={resumen.dias_con_movimiento}
            color="var(--yool-black)"
            hint="Días con al menos un cobro girable. Los demás no tienen nada que cerrar."
          />
          <FilaSalud
            label="Días limpios"
            // SIN `severidad` NO SE SABE, y `0` afirmaría que ninguno cerró limpio. Un `null`
            // sale como guion, que es lo que corresponde: la fila de al lado ya explica por
            // qué falta el dato.
            valor={sinSeveridad ? null : limpios}
            color="var(--badge-protegido-text)"
            hint="Cerraron y no dejaron ningún hueco de dato."
          />
          <FilaSalud
            label="Cuotas sin valorizar"
            valor={resumen.cuotas_sin_valorizar}
            color="var(--badge-pendiente-text)"
            // CUENTA TODO EL MES, cobradas o no: una cuota en UF sin valor de UF que
            // todavía no se cobra es el mismo hueco, y es la que dunning va a reintentar
            // con un monto que nadie decidió.
            hint={'Cuotas pactadas en UF sin valor de UF registrado, cobradas o no. Ese monto '
              + 'no se pudo calcular: no es cero.'}
          />
          <FilaSalud
            label="Asientos con error de Odoo"
            valor={resumen.asientos_con_error}
            color="var(--badge-vencido-text)"
            hint={'Odoo rechazó el asiento. No se resuelve esperando: hay que entrar al día y '
              + 'volver a contabilizarlo.'}
          />
        </div>
      </div>
    </div>
  );
}
